import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
// import Image from 'next/image';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';
import { useUserProvider } from '@/context/UserProvider';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import { debounce } from 'lodash-es';
import { Button, message } from 'antd';
import LocalImage from '@/components/Common/LocalImage';
import { useRouter } from 'next/router';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { SearchLimitLeftModel } from '@/types/home';
import { LimitAlert } from '@/components/LandingPage/FeeLimitAlert';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import FreeTimes from '@/components/LandingPage/FreeTimes';
import { GuestUsageModel } from '@/lib/hooks/common/useGuestViewerSearchUsageState';
import UserDashboard from '../../Dashboard/UserDashboard';
import { InsAccountModel } from '@/types/home';
import BannerAd from './banner-ad';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';

const TopSearchContent = ({
  onSearchResult,
  onUnlimitedClick,
  onLoginClick,
  data,
  hasCurrentModel,
  isGuestLocked,
  userUsage,
  guestUsage,
  consumeFreeTimes,
  consumeUserFreeTimes,
  fetchTimesRequest,
  syncRemainingTimesData,
  isLockClick,
  setIsLockClick,
  isDemoData,
}: {
  onSearchResult?: (model?: InstagramAnonymouslyModel) => void;
  onUnlimitedClick?: () => void;
  onLoginClick?: () => void;
  data?: any;
  hasCurrentModel?: boolean;
  isGuestLocked?: boolean;
  userUsage?: SearchLimitLeftModel;
  guestUsage?: GuestUsageModel;
  consumeFreeTimes?: () => void;
  consumeUserFreeTimes?: () => void;
  fetchTimesRequest?: (callback?: () => void, isLogin?: boolean) => void;
  syncRemainingTimesData?: (isLogin?: boolean) => void;
  isLockClick?: boolean;
  setIsLockClick?: (isLockClick: boolean) => void;
  isDemoData?: boolean;
}) => {
  const { t } = useTranslation();
  const [searchStr, setSearchStr, writeToStorage] = useMediaNameSync('');
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [loading, setLoading] = useState(false);
  const [loginState, setLoginState] = useState(isLogin);
  const router = useRouter();
  const [showLimitPage, setShowLimitPage] = useState(false);
  const { isPremium } = useStoryPremium();

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value) => {
        setLoginState(value);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  useEffect(() => {
    if (loginState) {
      fetchTimesRequest(() => {
        if (hasCurrentModel && isGuestLocked) {
          // 登录时，同步剩余次数
          syncRemainingTimesData(loginState);
        }
      }, loginState);
    }
  }, [loginState]);

  useEffect(() => {
    setLoginState(isLogin);

    if (!isLogin) {
      onSearchResult(null);
    } else {
      if (searchStr && isLockClick) {
        onSearchClick(searchStr);
        setIsLockClick(false);
      }
    }
  }, [isLogin]);

  const getNoStoriesDetectedContent = (img?: string, name?: string) => {
    return (
      <div style={{ paddingTop: '15px', marginBottom: '50px' }}>
        <p style={{ textAlign: 'center' }}>
          {t(
            "This account hasn't posted any Stories in the last 24 hours. Add it to your tracking list for long-term monitoring"
          )}
        </p>
        <div
          style={{
            background: '#F1EFFF',
            borderRadius: '24px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          <LocalImage
            src={img}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          />
          <span style={{ marginLeft: '10px', fontWeight: '500' }}>{name ?? ''}</span>
        </div>
      </div>
    );
  };

  const onSearchClick = (e?: string) => {
    // 增加未登录免费试用后，搜索时不再检查登录状态
    if (e?.length > 0) {
      // 手动写入搜索内容到 SessionStorage
      if (e.includes('/reel/') || e.includes('/reels/')) {
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      writeToStorage(e);

      if (loading) {
        return;
      }
      setLoading(true);
      fetchInstagramAnonymouslySearchData(e, null, isLogin ? 0 : isGuestLocked ? 1 : 0, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (!res.data.private_mark) {
              if (!isLogin) {
                // 未登录时，消耗免费次数并记录时间
                consumeFreeTimes();
              } else {
                // 登录时，消耗免费次数
                consumeUserFreeTimes();
              }
            }
            // 如果账号没有数据，并且没有关联该账号，则提示没有数据
            // if (!res?.data?.has_data && !res?.data?.has_add) {
            //   setAlertInfo({
            //     title: t('No Stories Detected'),
            //     desc: getNoStoriesDetectedContent(res?.data?.avatar, res?.data?.media_name),
            //     btnTitle: t('Add Account Now'),
            //     href: `/plan?media_name=${res?.data?.media_name}&avatar=${res?.data?.avatar}&media_url=${res?.data?.media_url}`,
            //     type: 'No-Stories-Detected',
            //     media_name: res?.data?.media_name,
            //     avatar: res?.data?.avatar,
            //     media_url: res?.data?.media_url,
            //   });
            //   return;
            // }
            if (onSearchResult && !res.data.private_mark) {
              onSearchResult(res?.data);
            }
            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });

              return;
            }

            if (onSearchResult) {
              const dom = document.getElementById('search-result-content');
              if (dom) {
                dom.scrollIntoView({ behavior: 'smooth' });
              }
              onSearchResult(res?.data);
            }
            // } else if (res?.code === 22001 || res?.code === 22006) {
          } else if (res?.code === 22001 || res?.code === 22006) {
            // setLimitLeftModel({...(limitLeftModel ?? {}), search_limit:0});
            fetchTimesRequest(undefined, isLogin);
            setShowLimitPage(true);

            // setAlertInfo({
            //   title: t('Out of Views!'),
            //   desc: t("Oops! You've used all your anonymous views for Instagram Stories."),
            //   btnTitle: t('Add Account Now'),
            //   href: `/dashboard`,
            //   type: 'Out-of-Views'
            // });
            // ga_logEvent(EventName.ViewInstagramStoriesAnonymously_OutOfViews)
          } else if (res?.code === 22002) {
            fetchTimesRequest(undefined, isLogin);
            onSearchResult(data.demoData);
            setSearchStr('');

            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
          } else if (res?.code === 22004) {
            fetchTimesRequest(undefined, isLogin);
            setSearchStr('');
            onSearchResult(data.demoData);
            // setAlertInfo({
            //   title: t('Private Account Detected'),
            //   desc: t(
            //     'The Instagram account you entered is private. DolphinRadar only provides stories and highlights for public accounts. Please enter a public account to continue.',
            //   ),
            //   btnTitle: t('Try Another Account'),
            //   type: 'Private-Account-Detected',
            // });
          } else {
            message.error(res?.message);
          }
        })
        .catch((error) => {})
        .finally(() => {
          setLoading(false);
        });
    } else {
    }
  };

  return (
    <>
      <div className={styles.content}>
        <h1 style={{ width: 0, height: 0, display: 'none' }}>{t('Anonymous Instagram Story Viewer')}</h1>
        <div className={styles.title} dangerouslySetInnerHTML={{ __html: t('Anonymous Instagram Story Viewer') }} />

        <p className={styles.subTitle}>
          {t(
            'Instagram Story viewer allows anyone to watch Instagram Stories from public accounts and save them anonymously and for free, with no app installation required. Just enter the Instagram username to explore, view, and download Stories and Highlights on any device. Try it now!'
          )}
        </p>

        <div>
          <div className={styles.inputContainer}>
            <input
              value={searchStr}
              onChange={(e) => setSearchStr(e.target.value)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter') {
                  setTimeout(() => {
                    onSearchClick(searchStr);
                  }, 100);
                }
              }}
              placeholder={t('Enter profile link or username')}
            />
            <Button
              id="search-now-button"
              loading={loading}
              style={{ background: '#725CFF', color: '#FFF', height: '100%' }}
              onClick={() => {
                onSearchClick(searchStr);
              }}
            >
              {t('Search Now')}
            </Button>
          </div>
        </div>

        {/* 免费次数 */}
        <FreeTimes
          leftModel={userUsage}
          onUnlimitedClick={onUnlimitedClick}
          guestUsage={guestUsage}
          onLoginClick={onLoginClick}
        />

        <div className={`pt-8`}>
          <div className={`text-sm text-gray-500`}>
            {t(
              'Dolphin Radar is not affiliated with Instagram™. We do not host any Instagram content. All rights belong to their respective owners.'
            )}
          </div>
          <div className={`text-sm text-gray-500`}>
            {t('We respect privacy — only publicly available content is accessible.')}
          </div>
        </div>
      </div>
      {!isDemoData && <BannerAd />}
      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            if (alertInfo.type == 'No-Stories-Detected') {
              setAlertInfo(null);
              router.push(
                `/anonymous-instagram-story-viewer?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`
              );
              return;
            }

            router.push(alertInfo?.href);
          }
          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
              ga_logEvent(EventName.ViewInstagramStoriesAnonymously_OutOfViews_AddAccountNow);
            } else {
              setAlertInfo(null);
            }
          } else {
            setAlertInfo(null);
          }
        }}
      />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            ga_logEvent(EventName.GA_search_now_limit_notify_clicked);
            setTimeout(() => {
              onUnlimitedClick && onUnlimitedClick();
            }, 20);
          }
        }}
      />
    </>
  );
};

export default TopSearchContent;
